/* eslint-disable no-console */
/* eslint-disable no-undef */
import { useState, useEffect, useCallback } from "react";
import { Form, Button, Image, Checkbox, Grid, Segment } from "semantic-ui-react";
import { useFormik } from "formik";
import { useDropzone } from "react-dropzone";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { Event, AcademicCycle, Grade, Level } from "../../../../api";
import { useAuth } from "../../../../hooks";
import { buildStorageUrl, withEmptyOption } from "../../../../utils";
import { showSuccess, showError } from "../../../../utils/sweetalert";
import { initialValues, validationSchema } from "./EventForm.form";
import "./EventForm.scss";

const eventController = new Event();
const academicCycleController = new AcademicCycle();
const gradeController = new Grade();
const levelController = new Level();

const notifyModeOptions = [
  { key: "none", text: "No notificar", value: "none" },
  { key: "all", text: "Todos los estudiantes", value: "all" },
  { key: "level", text: "Por nivel", value: "level" },
  { key: "grade", text: "Por grado", value: "grade" },
];

export function EventForm(props) {
  const { onClose, onReload, event } = props;
  const { accessToken } = useAuth();
  const [categories, setCategories] = useState([]);
  const [academicCycles, setAcademicCycles] = useState([]);
  const [grades, setGrades] = useState([]);
  const [levels, setLevels] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [categoriesRes, cyclesRes, gradesRes, levelsRes] =
          await Promise.all([
            eventController.getEventCategories(accessToken),
            academicCycleController.getAcademicCycles(),
            gradeController.getGrades(accessToken),
            levelController.getLevels(accessToken),
          ]);

        setCategories(
          categoriesRes.data.map((cat) => ({
            key: cat.id,
            text: cat.name,
            value: cat.id,
          }))
        );

        setAcademicCycles(
          (cyclesRes.data || cyclesRes).map((cycle) => ({
            key: cycle.id,
            text: cycle.name,
            value: cycle.id,
          }))
        );

        setGrades(gradesRes.data || gradesRes);

        setLevels(
          (levelsRes.data || levelsRes).map((level) => ({
            key: level.id,
            text: level.name,
            value: level.id,
          }))
        );
      } catch (error) {
        console.error("Error loading form data:", error);
      }
    })();
  }, [accessToken]);

  const formik = useFormik({
    initialValues: initialValues(event),
    validationSchema: validationSchema(),
    validateOnChange: false,
    onSubmit: async (formValue) => {
      try {
        const data = { ...formValue };

        // Generate slug if not provided
        if (!data.slug && data.title) {
          data.slug = data.title
            .toLowerCase()
            .normalize("NFD")
            .replace(/[\u0300-\u036f]/g, "")
            .replace(/[^a-z0-9]+/g, "-")
            .replace(/^-+|-+$/g, "");
        }

        if (!event) {
          const result = await eventController.createEvent(accessToken, data);
          if (data.notifyMode && data.notifyMode !== "none") {
            if (data.status === "published") {
              showSuccess(
                "Evento creado",
                `Se notificó a ${result.notifiedCount} estudiante(s).`
              );
            } else {
              showSuccess(
                "Evento creado",
                "El evento se guardó como borrador; no se enviaron notificaciones."
              );
            }
          } else {
            showSuccess("Evento creado", "El evento se creó correctamente.");
          }
        } else {
          const result = await eventController.updateEvent(
            accessToken,
            event.id,
            data
          );
          if (
            data.notifyMode &&
            data.notifyMode !== "none" &&
            result.notifiedCount > 0
          ) {
            showSuccess(
              "Evento actualizado",
              `Se notificó a ${result.notifiedCount} estudiante(s).`
            );
          } else {
            showSuccess(
              "Evento actualizado",
              "El evento se actualizó correctamente."
            );
          }
        }

        onReload();
        onClose();
      } catch (error) {
        console.error("Error saving event:", error);
        showError(
          "No se pudo guardar el evento",
          error?.msg || error?.message || "Ocurrió un error inesperado."
        );
      }
    },
  });

  const onDrop = useCallback((acceptedFiles) => {
    const file = acceptedFiles[0];
    formik.setFieldValue("imageUrl", URL.createObjectURL(file));
    formik.setFieldValue("imageFile", file);
  }, [formik]);

  const { getRootProps, getInputProps } = useDropzone({
    accept: {
      "image/jpeg": [".jpg", ".jpeg"],
      "image/png": [".png"],
      "image/webp": [".webp"],
    },
    onDrop,
  });

  const getImagePreview = () => {
    if (formik.values.imageFile) {
      return formik.values.imageUrl;
    } else if (formik.values.imageUrl) {
      return buildStorageUrl(formik.values.imageUrl);
    }
    return null;
  };

  const statusOptions = [
    { key: "draft", text: "Borrador", value: "draft" },
    { key: "published", text: "Publicado", value: "published" },
    { key: "cancelled", text: "Cancelado", value: "cancelled" },
    { key: "completed", text: "Completado", value: "completed" },
  ];

  const audienceOptions = [
    { key: "all", text: "Todos", value: "all" },
    { key: "students", text: "Estudiantes", value: "students" },
    { key: "teachers", text: "Docentes", value: "teachers" },
    { key: "parents", text: "Padres", value: "parents" },
    { key: "public", text: "Público", value: "public" },
  ];

  const quillModules = {
    toolbar: [
      [{ header: [1, 2, 3, 4, 5, 6, false] }],
      ["bold", "italic", "underline", "strike"],
      [{ list: "ordered" }, { list: "bullet" }],
      ["link", "image", "video"],
      [{ color: [] }, { background: [] }],
      [{ align: [] }],
      ["clean"],
    ],
  };

  return (
    <Form className="event-form" onSubmit={formik.handleSubmit}>
      <Grid>
        <Grid.Row>
          <Grid.Column width={16}>
            <div className="event-form__image" {...getRootProps()}>
              <input {...getInputProps()} />
              {getImagePreview() ? (
                <Image src={getImagePreview()} fluid />
              ) : (
                <div className="event-form__image-placeholder">
                  <span>Arrastra la imagen del evento aquí o haz clic para seleccionar</span>
                </div>
              )}
            </div>
          </Grid.Column>
        </Grid.Row>

        <Grid.Row>
          <Grid.Column width={16}>
            <Form.Input
              name="title"
              placeholder="Título del evento *"
              label="Título *"
              onChange={formik.handleChange}
              value={formik.values.title}
              error={formik.errors.title}
            />
          </Grid.Column>
        </Grid.Row>

        <Grid.Row>
          <Grid.Column width={16}>
            <Form.Input
              name="slug"
              placeholder="Se genera automáticamente a partir del título si se deja vacío"
              label="Slug (URL)"
              onChange={formik.handleChange}
              value={formik.values.slug}
              error={formik.errors.slug}
            />
          </Grid.Column>
        </Grid.Row>

        <Grid.Row>
          <Grid.Column width={16}>
            <Form.TextArea
              name="description"
              placeholder="Descripción corta del evento *"
              label="Descripción *"
              onChange={formik.handleChange}
              value={formik.values.description}
              error={formik.errors.description}
              rows={3}
            />
          </Grid.Column>
        </Grid.Row>

        <Grid.Row>
          <Grid.Column width={16}>
            <label>Contenido *</label>
            <ReactQuill
              theme="snow"
              value={formik.values.content}
              onChange={(value) => formik.setFieldValue("content", value)}
              modules={quillModules}
              placeholder="Contenido completo del evento..."
            />
            {formik.errors.content && (
              <div className="ui pointing red basic label">{formik.errors.content}</div>
            )}
          </Grid.Column>
        </Grid.Row>

        <Grid.Row>
          <Grid.Column width={8}>
            <Form.Input
              type="datetime-local"
              name="eventDate"
              label="Fecha de inicio *"
              onChange={formik.handleChange}
              value={formik.values.eventDate}
              error={formik.errors.eventDate}
            />
          </Grid.Column>
          <Grid.Column width={8}>
            <Form.Input
              type="datetime-local"
              name="endDate"
              label="Fecha de fin"
              onChange={formik.handleChange}
              value={formik.values.endDate}
              error={formik.errors.endDate}
            />
          </Grid.Column>
        </Grid.Row>

        <Grid.Row>
          <Grid.Column width={8}>
            <Checkbox
              label="Evento virtual"
              checked={formik.values.isVirtual}
              onChange={(e, data) => formik.setFieldValue("isVirtual", data.checked)}
            />
          </Grid.Column>
          <Grid.Column width={8}>
            <Checkbox
              label="Evento destacado"
              checked={formik.values.featured}
              onChange={(e, data) => formik.setFieldValue("featured", data.checked)}
            />
          </Grid.Column>
        </Grid.Row>

        {formik.values.isVirtual ? (
          <Grid.Row>
            <Grid.Column width={16}>
              <Form.Input
                name="virtualLink"
                placeholder="Enlace del evento virtual *"
                label="Enlace virtual *"
                onChange={formik.handleChange}
                value={formik.values.virtualLink}
                error={formik.errors.virtualLink}
              />
            </Grid.Column>
          </Grid.Row>
        ) : (
          <Grid.Row>
            <Grid.Column width={16}>
              <Form.Input
                name="location"
                placeholder="Ubicación del evento *"
                label="Ubicación *"
                onChange={formik.handleChange}
                value={formik.values.location}
                error={formik.errors.location}
              />
            </Grid.Column>
          </Grid.Row>
        )}

        <Grid.Row>
          <Grid.Column width={8}>
            <Form.Select
              className="edunova-select"
              name="categoryId"
              placeholder="Seleccione una categoría *"
              label="Categoría *"
              clearable
              options={withEmptyOption(categories, "Seleccione una categoría *")}
              onChange={(e, data) => formik.setFieldValue("categoryId", data.value)}
              value={formik.values.categoryId}
              error={formik.errors.categoryId}
            />
          </Grid.Column>
          <Grid.Column width={8}>
            <Form.Select
              className="edunova-select"
              name="status"
              placeholder="Estado *"
              label="Estado *"
              clearable
              options={withEmptyOption(statusOptions, "Estado *")}
              onChange={(e, data) => {
                formik.setFieldValue("status", data.value);
                if (data.value !== "published") {
                  formik.setFieldValue("notifyMode", "none");
                  formik.setFieldValue("notifyLevelId", null);
                  formik.setFieldValue("notifyGradeId", null);
                }
              }}
              value={formik.values.status}
              error={formik.errors.status}
            />
          </Grid.Column>
        </Grid.Row>

        <Grid.Row>
          <Grid.Column width={8}>
            <Form.Select
              className="edunova-select"
              name="targetAudience"
              placeholder="Audiencia objetivo *"
              label="Audiencia objetivo *"
              clearable
              options={withEmptyOption(audienceOptions, "Audiencia objetivo *")}
              onChange={(e, data) => formik.setFieldValue("targetAudience", data.value)}
              value={formik.values.targetAudience}
              error={formik.errors.targetAudience}
            />
          </Grid.Column>
          <Grid.Column width={8}>
            <Form.Input
              type="number"
              name="maxAttendees"
              placeholder="Límite de asistentes"
              label="Límite de asistentes"
              onChange={formik.handleChange}
              value={formik.values.maxAttendees}
              error={formik.errors.maxAttendees}
            />
          </Grid.Column>
        </Grid.Row>

        {formik.values.status === "published" && (
          <>
            <Grid.Row>
              <Grid.Column width={16}>
                <p className="event-form__notify-hint">
                  {!event
                    ? "Se notificará a los estudiantes al crear el evento como Publicado."
                    : "Se notificará a los estudiantes solo si el evento pasa de Borrador a Publicado con este cambio."}
                </p>
                <Form.Select
                  className="edunova-select"
                  name="notifyMode"
                  label="Notificar a estudiantes"
                  options={notifyModeOptions}
                  onChange={(e, data) => {
                    formik.setFieldValue("notifyMode", data.value);
                    formik.setFieldValue("notifyLevelId", null);
                    formik.setFieldValue("notifyGradeId", null);
                  }}
                  value={formik.values.notifyMode}
                  error={formik.errors.notifyMode}
                />
              </Grid.Column>
            </Grid.Row>

            {(formik.values.notifyMode === "level" ||
              formik.values.notifyMode === "grade") && (
              <Grid.Row>
                <Grid.Column width={16}>
                  <Form.Select
                    className="edunova-select"
                    name="notifyLevelId"
                    placeholder="Seleccione un nivel *"
                    label="Nivel a notificar *"
                    clearable
                    options={withEmptyOption(levels, "Seleccione un nivel *")}
                    onChange={(e, data) => {
                      formik.setFieldValue("notifyLevelId", data.value);
                      if (formik.values.notifyMode === "grade") {
                        formik.setFieldValue("notifyGradeId", null);
                      }
                    }}
                    value={formik.values.notifyLevelId}
                    error={formik.errors.notifyLevelId}
                  />
                </Grid.Column>
              </Grid.Row>
            )}

            {formik.values.notifyMode === "grade" && (
              <Grid.Row>
                <Grid.Column width={16}>
                  <Form.Select
                    className="edunova-select"
                    name="notifyGradeId"
                    placeholder="Seleccione un grado *"
                    label="Grado a notificar *"
                    clearable
                    disabled={!formik.values.notifyLevelId}
                    options={withEmptyOption(
                      grades
                        .filter(
                          (grade) =>
                            grade.levelId === formik.values.notifyLevelId
                        )
                        .map((grade) => ({
                          key: grade.id,
                          text: grade.name,
                          value: grade.id,
                        })),
                      "Seleccione un grado *"
                    )}
                    onChange={(e, data) =>
                      formik.setFieldValue("notifyGradeId", data.value)
                    }
                    value={formik.values.notifyGradeId}
                    error={formik.errors.notifyGradeId}
                  />
                </Grid.Column>
              </Grid.Row>
            )}
          </>
        )}

        <Grid.Row>
          <Grid.Column width={16}>
            <Button type="submit" primary fluid loading={formik.isSubmitting}>
              {!event ? "Crear Evento" : "Actualizar Evento"}
            </Button>
          </Grid.Column>
        </Grid.Row>
      </Grid>
    </Form>
  );
}
